import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';

ChartJS.register(...registerables);

// Custom plugin to draw percentage labels outside
const outsideLabelsPlugin = {
    id: 'outsideLabels',
    afterDraw: (chart) => {
        const { ctx, data, chartArea } = chart;
        const meta = chart.getDatasetMeta(0);

        if (!meta.data.length) return;

        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;
        const outerRadius = meta.data[0].outerRadius;

        // Define positions for the labels (top-right, bottom-left, left)
        const labelPositions = [
            { angle: -45, align: 'left' },     // Resolved (78.4%) - top right
            { angle: 180, align: 'right' },    // In Progress (15.6%) - left
            { angle: 135, align: 'right' }     // Pending (6%) - bottom left
        ];

        const percentages = chart.options.percentages || [];
        const colors = ['#34a853', '#fbbc04', '#ea4335'];

        meta.data.forEach((arc, index) => {
            if (index >= percentages.length) return;

            const startAngle = arc.startAngle;
            const endAngle = arc.endAngle;
            const midAngle = (startAngle + endAngle) / 2;

            // Calculate label position
            const labelRadius = outerRadius + 25;
            const x = centerX + Math.cos(midAngle) * labelRadius;
            const y = centerY + Math.sin(midAngle) * labelRadius;

            // Draw connecting dot on the arc
            const dotRadius = outerRadius + 3;
            const dotX = centerX + Math.cos(midAngle) * dotRadius;
            const dotY = centerY + Math.sin(midAngle) * dotRadius;

            ctx.save();
            ctx.beginPath();
            ctx.arc(dotX, dotY, 4, 0, Math.PI * 2);
            ctx.fillStyle = colors[index];
            ctx.fill();
            ctx.restore();

            // Draw percentage text
            ctx.save();
            ctx.font = 'bold 14px Inter, sans-serif';
            ctx.fillStyle = colors[index];
            ctx.textAlign = midAngle > Math.PI / 2 && midAngle < Math.PI * 1.5 ? 'right' : 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(`${percentages[index]}%`, x, y);
            ctx.restore();
        });
    }
};

const IssueResolutionStatusChart = ({ data = {} }) => {
    const resolutionStatusChartRef = useRef(null);
    const resolutionStatusChartInstance = useRef(null);

    // Extract data from props with defaults
    const {
        total_issues = 0,
        resolved = 0,
        in_progress = 0,
        pending = 0,
        resolved_percentage,
        in_progress_percentage,
        pending_percentage
    } = data;

    // Calculate percentages if not provided
    const total = resolved + in_progress + pending;
    const resolvedPercent = resolved_percentage
        ? parseFloat(resolved_percentage)
        : total > 0 ? Math.round((resolved / total) * 100 * 10) / 10 : 0;
    const inProgressPercent = in_progress_percentage
        ? parseFloat(in_progress_percentage)
        : total > 0 ? Math.round((in_progress / total) * 100 * 10) / 10 : 0;
    const pendingPercent = pending_percentage
        ? parseFloat(pending_percentage)
        : total > 0 ? Math.round((pending / total) * 100 * 10) / 10 : 0;

    useEffect(() => {
        if (resolutionStatusChartRef.current) {
            if (resolutionStatusChartInstance.current) {
                resolutionStatusChartInstance.current.destroy();
            }

            const ctx = resolutionStatusChartRef.current.getContext('2d');

            // Use actual counts for chart, but display percentages in tooltip
            const chartValues = total > 0 ? [resolved, in_progress, pending] : [33.3, 33.3, 33.4];

            resolutionStatusChartInstance.current = new ChartJS(ctx, {
                type: 'doughnut',
                data: {
                    labels: ['Resolved', 'In Progress', 'Pending'],
                    datasets: [
                        {
                            data: chartValues,
                            backgroundColor: ['#e6f4ea', '#fef7e0', '#fce8e6'],
                            borderColor: ['#34a853', '#fbbc04', '#ea4335'],
                            borderWidth: 2,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '65%',
                    layout: {
                        padding: {
                            top: 40,
                            bottom: 45,
                            left: 50,
                            right: 50
                        }
                    },
                    percentages: [resolvedPercent, inProgressPercent, pendingPercent],
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: function (context) {
                                    const index = context.dataIndex;
                                    const percentages = [resolvedPercent, inProgressPercent, pendingPercent];
                                    const counts = [resolved, in_progress, pending];
                                    return `${context.label}: ${counts[index]} (${percentages[index]}%)`;
                                },
                            },
                        },
                        datalabels: { display: false },
                    },
                },
                plugins: [outsideLabelsPlugin]
            });
        }
        return () => {
            if (resolutionStatusChartInstance.current) {
                resolutionStatusChartInstance.current.destroy();
            }
        };
    }, [data, resolved, in_progress, pending, resolvedPercent, inProgressPercent, pendingPercent, total]);

    const hasData = total > 0 || total_issues > 0;

    const legendItems = [
        { label: 'Resolved', color: '#34a853', bgColor: '#e6f4ea', count: resolved, percent: resolvedPercent },
        { label: 'In Progress', color: '#fbbc04', bgColor: '#fef7e0', count: in_progress, percent: inProgressPercent },
        { label: 'Pending', color: '#ea4335', bgColor: '#fce8e6', count: pending, percent: pendingPercent }
    ];

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#ffffff',
                border: '1px solid #e6e6e6',
                borderRadius: '20px',
                padding: '20px',
            }}
        >
            {/* Total Issues Summary - Centered in donut */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: { xs: '260px', sm: '280px', md: '300px' },
                    height: { xs: '180px', sm: '200px', md: '220px' },
                    position: 'relative',
                    flexShrink: 0,
                    margin: '0 auto'
                }}
            >
                {/* Center text inside donut */}
                {total_issues > 0 && hasData && (
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        zIndex: 1,
                        pointerEvents: 'none'
                    }}>
                        <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#333' }}>
                            {total_issues}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#666' }}>
                            Total Issues
                        </Typography>
                    </Box>
                )}
                {!hasData ? (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    }}>
                        <Typography sx={{ color: '#999', fontSize: '14px', textAlign: 'center' }}>
                            No resolution data available
                        </Typography>
                    </Box>
                ) : (
                    <canvas ref={resolutionStatusChartRef} style={{ width: '100%', height: '100%' }} />
                )}
            </Box>

            {/* Custom Legend in One Row */}
            <Box sx={{ display: 'flex', gap: '16px', mt: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {legendItems.map((item) => (
                    <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Box sx={{
                            width: '10px',
                            height: '10px',
                            bgcolor: item.bgColor,
                            border: `2px solid ${item.color}`,
                            borderRadius: '50%'
                        }} />
                        <Typography sx={{ fontSize: '12px', color: '#333', fontWeight: 500 }}>
                            {item.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default IssueResolutionStatusChart;
